import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import download from "../../../../assets/images/icons/download.svg";
import menu from "../../../../assets/images/icons/menu.svg";
import print from "../../../../assets/images/icons/print_black.svg";
import report_patient from "../../../../assets/images/icons/Report.svg";
import { Dropdown, GeneralButton } from "../../../../components";
import { EndPoints } from "../../../../Constants/endpoints";

import Ic_pdf from "../../../../assets/images/icons/pdf.svg";
import {
  useFilter,
  useGetData,
  useGetPersonalInfo,
  usePrintPdf,
} from "../../../../hooks";
import useToast from "../../../../hooks/useToast";
import { GetPatientInfo } from "../../../../interface/GeneralResponse";
import { handlePrint } from "../../../../utils/fileUtils";
import { AddReport, RemovePatient } from "../component";
import AddPrescriptionModal from "../component/AddPrescriptionModal";
import PatientProfileSidebar from "./PatientProfileSidebar";

const PatientProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {showToast}=useToast()
  const { searchParams, handleParamsAdd } = useFilter();

  const { t } = useTranslation();

  const [isExportPDF, setIsExportPDF] = useState<boolean>(false);
  const [medicalRecordId, setMedicalRecordId] = useState<number | null>(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
   const [isFetching, setIsFetching] = useState<boolean>(false);

  const closeReportModal = () => setIsReportModalOpen(false);
  const closeDeleteModal = () => setIsDeleteModalOpen(false);

  const handleReportClick = () => {
    setIsReportModalOpen(true);
  };

  const { data, isLoading } = useGetData<GetPatientInfo>(
    EndPoints.GetPatients,
    { refetchOnWindowFocus: true },
    id,
  );

  const PatientsData = data?.Data;

  const handleAddPrescription = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const {
    handleExportPDF,
    isLoading: isLoadingExport,
    isSuccess,
    data: fileData,
  } = usePrintPdf({
    endPoint: `${EndPoints.GenerateMedicalRecordWithFollowUpsPdf}`,
    id: Number(medicalRecordId),
    enabled: isExportPDF || isPrinting,
  });

  useEffect(() => {
    if (isSuccess && isExportPDF) {
      handleExportPDF().then(() => {
        setIsExportPDF(false);
      });
    }
  }, [isSuccess, isExportPDF]);

    const { data:recordData, isLoading:isRecordLoading, isSuccess:isRecordSuccess, isError } = useGetData<any>(
      EndPoints.PatientsReports,
      {
        filter: {
          PatientId: `${id}`,
        },
        enabled: isFetching && !!id,
      },
    );

      const { handleExportPDF:handleExportRecordPDF } = usePrintPdf({
    enabled: false,
    file: recordData,
  });

  useEffect(() => {
    if (isRecordSuccess && isFetching) {
      handleExportRecordPDF()
        .then(() => {
          showToast(t("file_downloaded_successfully"), "success");
          setIsFetching(false);
        })
        .catch(() => {
          setIsFetching(false);
        });
    }
    if (isError && isFetching) {
      showToast(t("error"), "error");
      setIsFetching(false);
    }
  }, [isRecordSuccess, isError, isFetching]);




  useEffect(() => {
    if (isSuccess && isPrinting) {
      handlePrint(fileData?.Data?.FileContents, "PDF", setIsPrinting);
    }
  }, [isSuccess, isPrinting]);
  const { isAllowed } = useGetPersonalInfo();

  const operations: any[] = [
    isLoading
      ? {
          className: `w-60 rounded-lg bg-body-text animate-pulse h-7`,
        }
      : !data?.Data?.HasMedicalRecord
        ? {
            label: t("add_medical_report"),
            className: `w-60 hover:!text-primary font-semibold`,
            onClick: () => {
              navigate(`/patient/${id}/add-medical-record`);
            },
          }
        : undefined,
  

    {
      label: t("book_appointment"),
      onClick: () => navigate(`/appointments/add_new_appointment/${id}`),
      className: `w-60 hover:!text-primary font-semibold`,
    },
    {
      label: t("add_prescriptions"),
      onClick: handleAddPrescription,
      className: `w-60 hover:!text-primary font-semibold`,
    },
    {
      icon: download,
      onClick: () => setIsExportPDF(true),
      label: t("export_medical_record_as_a_PDF"),
      className: `w-60 hover:!text-primary font-semibold`,
      hide: !data?.Data?.HasMedicalRecord,
    },
      {
      icon: Ic_pdf,
      label: t("export_patient_report"),
      className: `items-center !text-body-text gap-2 rounded-md py-3 px-2`,
      onClick: () => {
        !isRecordLoading && setIsFetching(true);
      },
    },
    {
      icon: print,
      label: t("print_record"),
      onClick: () => setIsPrinting(true),
      className: `w-60 hover:!text-primary font-semibold`,
      hide: !data?.Data?.HasMedicalRecord,
    },
    // {
    //   icon: remove,
    //   label: t("remove"),
    //   onClick: handleDeleteClick,
    //   className: `w-60 !text-error font-semibold hover:bg-red-100`,
    // },
    {
      icon: report_patient,
      label: t("report_patient"),
      onClick: handleReportClick,
      className: `w-60 !text-error font-semibold hover:bg-red-100`,
    },
  ]?.filter(Boolean);

  useEffect(() => {
    if (searchParams.get("openAppointment") === "true") {
      navigate(`/appointments/add_new_appointment/${id}`);
      handleParamsAdd("openAppointment", false);
    }
  }, [searchParams]);
  return (
    <div className="relative flex flex-wrap gap-4 lg:flex-nowrap">
      <PatientProfileSidebar />
      <div className="box w-3/4 min-w-96 overflow-auto !p-4 max-lg:hidden">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-primary-textFont font-bold">
            {t("patient_profile")}
          </h1>
          {isAllowed && (
            <div className="flex items-center gap-2">
              <Dropdown
                menuItems={operations}
                children={
                  <img
                    src={menu}
                    alt="menu"
                    className="cursor-pointer rounded-lg border border-radio px-4 py-2.5"
                    onClick={() => setIsOpen(!isOpen)}
                  />
                }
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                className="w-80"
              />
              {PatientsData?.HasMedicalRecord && medicalRecordId && (
                <GeneralButton
                  className="!h-full border border-primary"
                  onClick={() =>
                    navigate(
                      `/patients_list/${id}/medical-records/${medicalRecordId}/new-followup`,
                    )
                  }
                  color="lite"
                  title={t("follow_up")}
                />
              )}
              <GeneralButton
                className="!px-4"
                onClick={() => navigate(`/patient/${id}/add-medical-record`)}
                title={t("new_medical_report")}
              />
            </div>
          )}
        </div>

        <Outlet context={{ setMedicalRecordId }} />
      </div>
      {isModalOpen && (
        <AddPrescriptionModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          patientData={PatientsData}
        />
      )}
      <AddReport
        Id={Number(id)}
        isReportModalOpen={isReportModalOpen}
        onClose={closeReportModal}
      />
      <RemovePatient
        name={PatientsData?.FullName || ""}
        id={PatientsData?.Id || ""}
        navigate={() => {
          navigate("/patients_list");
        }}
        closeDeleteModal={closeDeleteModal}
        isDeleteModalOpen={isDeleteModalOpen}
      />
    </div>
  );
};

export default PatientProfile;
